import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { getApiBaseUrl } from '../config';
import { RootState } from '../store/store';
import { GetProductsResult } from './types';
import { WixProduct } from '../types';

export const getToken = (state: RootState): string => {
  return state.app.wixUser.accessToken || '';
};

type ProductsInitialPageParam = {
  cursor?: string;
  limit: number;
  offset?: number;
};

const PRODUCTS_LIMIT_PER_PAGE = 50;

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiBaseUrl(),
    prepareHeaders: (headers, api) => {
      const state = api.getState() as RootState;
      const token = getToken(state);

      token && headers.set('X-Auth-Token', token);

      return headers;
    },
  }),
  tagTypes: ['User', 'ProductList', 'Product'],
  endpoints: (builder) => ({
    getProducts: builder.infiniteQuery<GetProductsResult, string, ProductsInitialPageParam>({
      infiniteQueryOptions: {
        initialPageParam: { limit: PRODUCTS_LIMIT_PER_PAGE },
        getNextPageParam: (lastPage, allPages, lastPageParam, allPageParams) => {
          if (!lastPage.hasNext) {
            return undefined;
          }

          if (lastPage.offset) {
            return {
              ...lastPageParam,
              offset: lastPage.offset,
            };
          }

          return {
            ...lastPageParam,
            cursor: lastPage.cursor,
          };
        },
      },
      query({ pageParam: { cursor, limit, offset } }) {
        const urlParams = new URLSearchParams();

        urlParams.append('limit', limit.toString());

        if (cursor) {
          urlParams.append('cursor', cursor);
        } else if (offset) {
          urlParams.append('offset', offset.toString());
        }

        return `product/list?${urlParams.toString()}`;
      },
    }),
    getProduct: builder.query<
      { product: WixProduct; productUpload?: { framesAmount: number; uploadId: string } },
      { productId: string }
    >({
      query: ({ productId }) => {
        return `product/${productId}`;
      },
      providesTags: (result) => [{ type: 'Product', id: result?.product.id }],
    }),
    deleteProductUpload: builder.mutation<void, { productId: string }>({
      query: ({ productId }) => ({
        method: 'delete',
        url: `product/${productId}`,
      }),
      invalidatesTags: (result, error, { productId }) => [{ type: 'Product', id: productId }],
    }),
  }),
});
